import React from 'react';
import { Table } from 'antd';
import Spin from 'src/components/common/Spin';
import { getColumns } from './MPITable.config';
import ProductSummarySegment from '../../types/ProductSummarySegment';
import FormattedNumber from 'src/components/common/FormattedNumber';
import styles from './MPITable.module.less';

type MPITableProps = {
  loading: boolean;
  isPriceRegular: boolean;
  productSummaryList: ProductSummarySegment[];
};

const emptyText: React.ReactNode = <p className={styles.noResults}>No results found</p>;

const getAveragePricePercent = (prices: number[], minPrice: number, maxPrice: number): number => {
  if (prices.length > 0 && maxPrice > minPrice) {
    const averagePrice = prices.reduce((a, b) => a + b) / prices.length;
    return (100 * (averagePrice - minPrice)) / (maxPrice - minPrice);
  }
  return 0;
};

const XLine = ({ minPrice, maxPrice }: { minPrice: number; maxPrice: number }) => {
  const steps = [0, 25, 50, 75, 100];

  const getPrice = (percent: number) => {
    return (maxPrice - minPrice) * percent + minPrice;
  };

  const prices = [minPrice, getPrice(0.25), getPrice(0.5), getPrice(0.75), maxPrice];

  return (
    <div className={styles.xLine}>
      <div className={styles.line}>
        {steps.map((i) => (
          <div className={styles.dot} style={{ left: `${i}%` }} key={i}></div>
        ))}
        {steps.map((step, i) => (
          <div className={styles.price} style={{ left: `${step}%` }} key={step}>
            <FormattedNumber type="currency" currency={'USD'} value={prices[i]} />
          </div>
        ))}
      </div>
    </div>
  );
};

const MPITable: React.FC<MPITableProps> = ({ productSummaryList, loading, isPriceRegular }) => {
  // Filter the product summary list based on the price type and ownerId condition
  const filteredProductSummaryList = productSummaryList.filter(item => {
    if (isPriceRegular) {
      return item.regularPrice !== null || item.ownerId === 0;
    } else {
      return item.lowestPrice !== null || item.ownerId === 0;
    }
  });

  const prices = filteredProductSummaryList.map((item) => (isPriceRegular ? item.regularPrice : item.lowestPrice));
  const minPrice = Math.min(...prices);
  const maxPrice = Math.max(...prices);
  const averagePricePercent = getAveragePricePercent(prices, minPrice, maxPrice);

  return (
    <div className={styles.MPITableContainer}>
      <Spin spinning={loading}>
        <div className={styles.MPITableWrapper}>
          <Table
            className={styles.MPITable}
            rowKey={(_, index: number): string => String(index)}
            columns={getColumns(minPrice, maxPrice, averagePricePercent, isPriceRegular)}
            dataSource={filteredProductSummaryList}
            pagination={false}
            tableLayout={'auto'}
            locale={{ emptyText }}
            scroll={{ y: 250 }}
            rowClassName={(_record, index) => (index === 0 ? styles.stickyRow : '')}
          />
        </div>
      </Spin>
      <XLine minPrice={minPrice} maxPrice={maxPrice} />
    </div>
  );
};

export default MPITable;
